import {
  calculateJobPayoutSplit,
  type JobPayoutSplitInput,
  type JobPayoutSplitResult,
} from '../core/financial';
import {
  resolveHoldbackAdjustment,
  type HoldbackAdjustment,
  type HoldbackPayoutRow,
} from '../core/holdbackAdjustment';

export interface JobPayoutSplitAfterHeldRefunds extends JobPayoutSplitResult {
  /** What held refunds took (or, before the final release, will take) off the holdback. */
  holdbackAdjustment: HoldbackAdjustment;
}

/**
 * calculateJobPayoutSplit, less what pre-release held refunds took off the holdback
 * (Max, 2026-09-22 — see core/holdbackAdjustment.ts). The final release pays the holdback
 * less the platform-funded part of each held refund made before it, so:
 *
 *  - before the final release, the amount still PENDING to the trade is that much smaller;
 *  - after it (FULLY_RELEASED), the amount COMPLETED is that much smaller — read from what
 *    the FINAL_RELEASE row recorded, so it is exactly what was paid.
 *
 * `payoutRows` are the job's payout_transactions rows (HELD_REFUND, and FINAL_RELEASE once
 * there is one); with none it is identical to calculateJobPayoutSplit. Kept apart from
 * core/holdbackAdjustment.ts, which the Deno edge functions import and so cannot take an
 * extension-less import of core/financial.
 */
export function calculateJobPayoutSplitAfterHeldRefunds(
  input: JobPayoutSplitInput & { payoutRows?: readonly HoldbackPayoutRow[] | null },
): JobPayoutSplitAfterHeldRefunds {
  return applyHeldRefundsToSplit(calculateJobPayoutSplit(input), input);
}

/**
 * The same adjustment applied to a split already computed with calculateJobPayoutSplit (so a
 * caller can keep its own split call and just pass the result through).
 */
export function applyHeldRefundsToSplit(
  split: JobPayoutSplitResult,
  input: { payoutStatus?: string | null; payoutRows?: readonly HoldbackPayoutRow[] | null },
): JobPayoutSplitAfterHeldRefunds {
  const holdbackAdjustment = resolveHoldbackAdjustment({
    holdbackCents: split.holdbackTotalCents,
    holdbackGstCents: split.holdbackGstCents,
    rows: input.payoutRows ?? [],
  });

  const deductedGst = holdbackAdjustment.deducted_gst_cents;
  const deductedNet = holdbackAdjustment.deducted_cents - deductedGst;
  if (holdbackAdjustment.deducted_cents <= 0) return { ...split, holdbackAdjustment };

  const status = input.payoutStatus || 'PENDING';
  if (status === 'DISPUTE_SETTLED') {
    return {
      ...split,
      holdbackAdjustment,
    };
  }
  if (status === 'FULLY_RELEASED') {
    return {
      ...split,
      completedAmountCents: Math.max(0, split.completedAmountCents - deductedNet),
      completedGstCents: Math.max(0, split.completedGstCents - deductedGst),
      holdbackAdjustment,
    };
  }
  return {
    ...split,
    pendingAmountCents: Math.max(0, split.pendingAmountCents - deductedNet),
    pendingGstCents: Math.max(0, split.pendingGstCents - deductedGst),
    holdbackAdjustment,
  };
}

/** Group payout rows by job_id (the shape the admin dashboards fetch them in). */
export function groupPayoutRowsByJob<T extends { job_id: string }>(rows: readonly T[] | null | undefined): Map<string, T[]> {
  const byJob = new Map<string, T[]>();
  for (const row of rows ?? []) {
    const list = byJob.get(row.job_id);
    if (list) list.push(row);
    else byJob.set(row.job_id, [row]);
  }
  return byJob;
}
